import { useEffect, useRef } from 'react';
import {
  useNodesInitialized,
  useReactFlow,
  type Edge,
  type Node,
} from '@xyflow/react';
import { WORKFLOW_FLOW_TRIGGER_TYPE } from '../components';
import { layoutFlowGraph } from '../workflow-graph-to-flow';
import {
  applyWorkflowFlowManualPositions,
  type WorkflowFlowManualPositions,
} from '../workflow-flow-manual-positions';
import {
  WORKFLOW_FLOW_NODE_HEIGHT,
  WORKFLOW_FLOW_NODE_WIDTH,
  WORKFLOW_FLOW_TRIGGER_HEIGHT,
  WORKFLOW_FLOW_TRIGGER_WIDTH,
} from '../workflow-flow-layout.constants';

function fallbackNodeDimensions(node: Node): { width: number; height: number } {
  if (node.type === WORKFLOW_FLOW_TRIGGER_TYPE) {
    return {
      width: WORKFLOW_FLOW_TRIGGER_WIDTH,
      height: WORKFLOW_FLOW_TRIGGER_HEIGHT,
    };
  }
  return {
    width: WORKFLOW_FLOW_NODE_WIDTH,
    height: WORKFLOW_FLOW_NODE_HEIGHT,
  };
}

function measuredNodeDimensions(node: Node): { width: number; height: number } {
  const width = node.measured?.width;
  const height = node.measured?.height;
  if (width !== undefined && height !== undefined) {
    return { width, height };
  }
  return fallbackNodeDimensions(node);
}

function allNodesMeasured(nodes: Node[]): boolean {
  return nodes.every(
    (node) =>
      node.measured?.width !== undefined && node.measured?.height !== undefined
  );
}

/** Fingerprint of DOM-measured node sizes; `:?` marks nodes not yet measured. */
export function buildFlowMeasurementKey(nodes: readonly Node[]): string {
  return nodes
    .map((node) => {
      const width = node.measured?.width;
      const height = node.measured?.height;
      if (width !== undefined && height !== undefined) {
        return `${node.id}:${width}x${height}`;
      }
      return `${node.id}:?`;
    })
    .join('|');
}

type UseMeasuredFlowLayoutArgs = {
  /** Resets the layout cache when graph topology changes. */
  graphStructureKey: string;
  /** Changes when React Flow finishes measuring nodes (incl. lazy card content). */
  measurementKey: string;
  layoutEdges: Edge[];
  manualPositions?: WorkflowFlowManualPositions;
  setNodes: (nodes: Node[]) => void;
};

/**
 * Second-pass dagre layout using React Flow DOM measurements. Pass 1 uses fixed
 * estimates from {@link workflowGraphToFlow}; once `node.measured` is available
 * we re-layout so taller cards (agent tools, webhook bodies, etc.) do not overlap.
 */
export function useMeasuredFlowLayout({
  graphStructureKey,
  measurementKey,
  layoutEdges,
  manualPositions = new Map(),
  setNodes,
}: UseMeasuredFlowLayoutArgs): void {
  const nodesInitialized = useNodesInitialized();
  const { getNodes } = useReactFlow();
  const lastAppliedKeyRef = useRef('');

  useEffect(() => {
    lastAppliedKeyRef.current = '';
  }, [graphStructureKey]);

  useEffect(() => {
    if (nodesInitialized !== true) {
      return;
    }
    if (measurementKey.includes(':?')) {
      return;
    }

    const layoutKey = `${graphStructureKey}::${measurementKey}`;
    if (layoutKey === lastAppliedKeyRef.current) {
      return;
    }

    const currentNodes = getNodes();
    if (currentNodes.length === 0) {
      return;
    }
    if (allNodesMeasured(currentNodes) !== true) {
      return;
    }

    lastAppliedKeyRef.current = layoutKey;

    const { nodes: layoutedNodes } = layoutFlowGraph(
      currentNodes,
      layoutEdges,
      measuredNodeDimensions
    );

    setNodes(applyWorkflowFlowManualPositions(layoutedNodes, manualPositions));
  }, [
    nodesInitialized,
    graphStructureKey,
    measurementKey,
    layoutEdges,
    manualPositions,
    getNodes,
    setNodes,
  ]);
}
